import * as React from 'react';
import { NotebookContext } from '../../../context/NotebookContext';
import client from '../../../lib/client';
import { ISubject } from '../../../lib/helpers/types';
import { groupCreateOrUpdateMutation } from '../../../queries/groups';
import { Switch } from '../../forms';
import { Card } from '../../ui';
import { Group } from './types';
import {
  GroupCreateOrUpdateMutation,
  GroupCreateOrUpdateMutationVariables
} from '../../../queries/__generated__/GroupCreateOrUpdateMutation';
import { ErrorMessagesArray } from '../tracker/types';
import ErrorMessages from '../../ErrorMessages';
import NewSubjectModal from '../../NewSubjectModal';
import { useEffect } from 'react';
import { notebookQuery } from '../../../queries/notebooks';

interface GroupSubjectsProps {
  group: Group;
  isUserGroupOwner: boolean;
}

const GroupSubjects: React.FC<GroupSubjectsProps> = ({ group, isUserGroupOwner }) => {
  const { notebook, reloadNotebook } = React.useContext(NotebookContext);
  const [groupSubjectIds, setGroupSubjectIds] = React.useState(group.subjects.map(s => s.id));
  const [errors, setErrors] = React.useState<ErrorMessagesArray>([]);
  const [showNewModal, setShowNewModal] = React.useState(false);

  useEffect(() => {
    setGroupSubjectIds(group.subjects.map(s => s.id));
  }, [group]);

  async function saveGroup(newSubjectIds?: string[]) {
    const res = await client.mutate<
      GroupCreateOrUpdateMutation,
      GroupCreateOrUpdateMutationVariables
    >({
      mutation: groupCreateOrUpdateMutation,
      variables: {
        input: {
          id: group.id,
          notebookId: notebook.id,
          subjectIds: newSubjectIds
        }
      },
      refetchQueries: [
        {
          query: notebookQuery,
          variables: { id: notebook.id }
        }
      ]
    });

    if (res.data!.createOrUpdateGroup.errors) {
      setErrors(res.data!.createOrUpdateGroup.errors);
      return;
    }

    if (errors.length) {
      setErrors([]);
    }

    reloadNotebook();
  }

  function toggleSubject(subjectId: string) {
    if (groupSubjectIds.includes(subjectId)) {
      const updatedSubjects = groupSubjectIds.filter((id: string) => id !== subjectId);
      saveGroup(updatedSubjects);
    } else {
      saveGroup(groupSubjectIds.concat(subjectId));
    }
  }

  return (
    <Card className="pa4 mb2">
      <div className="flex justify-between">
        <h2 className="f5 fw6 mv0">Subjects</h2>
        {isUserGroupOwner && (
          <button
            className="f7 blue hover-navy pointer"
            onClick={() => setShowNewModal(true)}
            data-testid="new-subject-button"
          >
            New Subject
          </button>
        )}
      </div>

      <ErrorMessages errors={errors} />

      {notebook.subjects.length > 0 && (
        <ul className="list pa0 mb0" data-testid="subjects-list">
          {notebook.subjects.map((subject: ISubject) => (
            <li
              key={subject.id}
              data-testid={subject.slug}
              className="pv2 bt b--light-gray flex items-center justify-between"
            >
              <span className="f6">{subject.name}</span>
              <Switch
                active={groupSubjectIds.includes(subject.id)}
                onClick={() => isUserGroupOwner && toggleSubject(subject.id)}
                disabled={!isUserGroupOwner}
              />
            </li>
          ))}
        </ul>
      )}

      {notebook.subjects.length === 0 && (
        <ul className="list pa0 mb0" data-testid="subjects-list-empty">
          <li className="i moon-gray flex justify-between items-center f6 serif lh-copy bt b--light-gray pv2">
            You don&rsquo;t have any subjects yet.
          </li>
        </ul>
      )}

      {showNewModal && <NewSubjectModal closeModal={() => setShowNewModal(false)} group={group} />}
    </Card>
  );
};

export default GroupSubjects;
